import {
  createSelector,
} from 'reselect';
import i18n from 'i18next';
import {
  getStatus,
} from 'redux-resource';

import {
  coreSelectors,
} from '@gymcloud/core';

import { getUiState } from './ui';

export const getSidebarProFolders = createSelector(
  [
    coreSelectors.getTreeFoldersWithItems('exercises'),
    coreSelectors.getTreeFoldersWithItems('documents'),
    coreSelectors.getTreeFoldersWithItems('workoutTemplates'),
    coreSelectors.getTreeFoldersWithItems('programTemplates'),
    getUiState('namespace'),
    coreSelectors.getResourceMeta('conversations', 'unread'),
    store => getStatus(store, 'folders.requests.fetchLibrary.status'),
  ],
  (
    exercises,
    documents,
    workoutTemplates,
    programTemplates,
    namespace,
    unreadChat,
    fetchLibraryStatus,
  ) => {
    const { features } = namespace.settings;

    const items = [
      {
        icon: 'dashboard',
        link: 'welcome',
        name: 'Dashboard',
      },
      {
        icon: 'clients',
        link: 'clients/individuals',
        name: i18n.t('client_plural'),
        additionalPaths: [
          'users',
          'client_groups',
          'personal_programs',
          'personal_workouts',
        ],
        children: [
          {
            icon: 'clients',
            link: 'clients/individuals',
            name: 'Individuals',
          },
          {
            icon: 'groups',
            link: 'clients/groups',
            name: 'Groups',
          },
          {
            icon: 'clients',
            link: 'clients/archived',
            name: 'Archived',
          },
        ],
      },
      {
        icon: 'messages',
        link: 'messages',
        name: 'Messages',
        badge: unreadChat?.count > 0 ? unreadChat.count : null,
        isHidden: !features.messages,
      },
      {
        icon: 'calendar',
        link: 'calendar',
        name: 'Calendar',
        isHidden: !features.calendar_and_scheduling,
      },
      {
        icon: 'billing',
        link: 'billing/invoices',
        name: 'Billing',
        isHidden: !features.billing_payment,
      },
      {
        ...exercises,
        icon: 'exercises',
        link: 'exercises',
        name: 'Exercises',
        isHidden: !fetchLibraryStatus.succeeded,
      },
      {
        ...workoutTemplates,
        icon: 'workout2',
        link: 'workout_templates',
        name: i18n.t('workoutTemplates'),
        isFolder: true,
        isHidden: !fetchLibraryStatus.succeeded,
      },
      {
        ...programTemplates,
        icon: 'programs',
        link: 'program_templates',
        name: 'Program Templates',
        isFolder: true,
        isHidden: !fetchLibraryStatus.succeeded,
      },
      {
        icon: 'videogallery',
        link: 'videos/recent',
        name: 'Video Library',
        isHidden: !features.video_library,
      },
      {
        ...documents,
        icon: 'documents',
        link: 'documents',
        name: 'Documents',
        isFolder: true,
        isHidden: !features.documents || !fetchLibraryStatus.succeeded,
      },
      {
        icon: 'trainings',
        link: 'users/me',
        name: 'My Training Log',
      },
      {
        icon: 'properties',
        link: 'personal_properties',
        name: 'Metrics',
      },
      {
        icon: 'marketplace',
        link: 'marketplace',
        name: 'Marketplace',
        isHidden: namespace.symbol === 'gymcloud' || !features.billing_payment,
      },
    ];

    return items.filter(item => !item.isHidden);
  },
);

export const getSidebarClientFolders = createSelector(
  [
    coreSelectors.getFolderItemsSortedByName('personalExercises'),
    coreSelectors.getFolderItemsSortedByName('personalWarmups'),
    coreSelectors.getFolderItemsSortedByName('personalWorkouts'),
    coreSelectors.getFolderItemsSortedByName('personalCooldowns'),
    coreSelectors.getFolderItemsSortedByName('personalPrograms'),
    getUiState('namespace'),
  ],
  (
    personalExercises,
    personalWarmups,
    personalWorkouts,
    personalCooldowns,
    personalPrograms,
    namespace,
  ) => {
    const { features } = namespace.settings;

    const items = [
      {
        icon: 'dashboard',
        link: 'welcome',
        name: 'Dashboard',
      },
      {
        icon: 'exercises',
        link: 'personal_exercises',
        name: 'Exercises',
        children: personalExercises,
      },
      {
        icon: 'warmups',
        link: 'personal_warmups',
        name: 'Warmups',
        children: personalWarmups,
      },
      {
        icon: 'workout2',
        link: 'personal_workouts',
        name: i18n.t('Workouts'),
        children: personalWorkouts,
      },
      {
        icon: 'cooldowns',
        link: 'personal_cooldowns',
        name: 'Cooldowns',
        children: personalCooldowns,
      },
      {
        icon: 'programs',
        link: 'personal_programs',
        name: 'Programs',
        children: personalPrograms,
      },
      {
        icon: 'videogallery',
        link: 'videos/recent',
        name: 'Video Library',
        isHidden: !features.video_library,
      },
      {
        icon: 'documents',
        link: 'documents',
        name: 'Documents',
        isHidden: !features.documents,
      },
      {
        icon: 'marketplace',
        link: 'marketplace',
        name: 'Marketplace',
        isHidden: namespace.symbol === 'gymcloud' || !features.billing_payment,
      },
    ];

    return items.filter(item => !item.isHidden);
  },
);
